import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import { haptic } from "../telegram";
import { useBackNavigation } from "../use-back-navigation";

type BlockedUser = { id: string; displayName: string; blockedAt: string };
type BlockPage = { blockedUsers: BlockedUser[]; nextCursor: string | null };

export function BlockedUsers({ onClose }: { onClose(): void }) {
  const [items, setItems] = useState<BlockedUser[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const panelRef = useRef<HTMLElement>(null);
  useBackNavigation(onClose, !(busyId !== null), panelRef);
  const closeButton = useRef<HTMLButtonElement>(null);
  const busyRef = useRef(false);
  busyRef.current = busyId !== null;

  const loadInitial = useCallback(async () => {
    setItems(null);
    setError("");
    try {
      const result = await api<BlockPage>("/v1/me/blocks?limit=30");
      setItems(result.blockedUsers);
      setCursor(result.nextCursor);
    } catch {
      setItems([]);
      setCursor(null);
      setError("Не удалось загрузить список. Проверьте соединение.");
    }
  }, []);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busyRef.current) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [onClose]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    setError("");
    try {
      const result = await api<BlockPage>(`/v1/me/blocks?limit=30&before=${encodeURIComponent(cursor)}`);
      setItems((current) => [...(current ?? []), ...result.blockedUsers]);
      setCursor(result.nextCursor);
    } catch {
      setError("Не удалось загрузить следующую страницу.");
    } finally {
      setLoadingMore(false);
    }
  };

  const unblock = async (user: BlockedUser) => {
    if (busyId) return;
    setBusyId(user.id);
    setError("");
    try {
      await api(`/v1/users/${user.id}/block`, { method: "DELETE" });
      setItems((current) => current?.filter((item) => item.id !== user.id) ?? []);
      setConfirmId(null);
      haptic("success");
    } catch {
      setError("Не удалось снять блокировку. Попробуйте ещё раз.");
    } finally {
      setBusyId(null);
    }
  };

  return createPortal(
    <div className="privacy-layer">
      <button
        type="button"
        className="privacy-backdrop"
        aria-label="Закрыть приватность"
        onClick={() => {
          if (!busyId) onClose();
        }}
      />
      <section ref={panelRef} className="privacy-panel" role="dialog" aria-modal="true" aria-labelledby="privacy-title">
        <header className="privacy-header">
          <div>
            <span className="eyebrow">Настройки</span>
            <h2 id="privacy-title">Заблокированные</h2>
          </div>
          <button type="button" ref={closeButton} disabled={busyId !== null} aria-label="Назад" onClick={onClose}>
            ‹
          </button>
        </header>
        <p className="privacy-description">
          Блокировка скрывает анкеты и переписки. После разблокировки существующие совпадения могут снова появиться, а
          удалённые лайки не восстанавливаются.
        </p>
        {items === null ? (
          <p className="privacy-status" role="status">
            Загружаем список…
          </p>
        ) : (
          <div className="privacy-users">
            {items.map((user) => (
              <div className="privacy-user" key={user.id}>
                <div className="privacy-user-copy">
                  <strong>{user.displayName}</strong>
                  <small>Заблокирован {new Date(user.blockedAt).toLocaleDateString("ru-RU")}</small>
                </div>
                {confirmId === user.id ? (
                  <div className="privacy-confirm">
                    <span>Разблокировать?</span>
                    <button type="button" disabled={busyId !== null} onClick={() => setConfirmId(null)}>
                      Отмена
                    </button>
                    <button type="button" disabled={busyId !== null} onClick={() => void unblock(user)}>
                      {busyId === user.id ? "Снимаем…" : "Подтвердить"}
                    </button>
                  </div>
                ) : (
                  <button type="button" disabled={busyId !== null} onClick={() => setConfirmId(user.id)}>
                    Разблокировать
                  </button>
                )}
              </div>
            ))}
            {!items.length && !error && <div className="privacy-empty">Пока нет заблокированных пользователей.</div>}
            {cursor && (
              <button
                type="button"
                className="secondary-button privacy-more"
                disabled={loadingMore || busyId !== null}
                onClick={() => void loadMore()}
              >
                {loadingMore ? "Загружаем…" : "Показать ещё"}
              </button>
            )}
          </div>
        )}
        {error && (
          <div className="privacy-error" role="alert">
            <p>{error}</p>
            {items?.length === 0 && (
              <button type="button" className="secondary-button" onClick={() => void loadInitial()}>
                Повторить
              </button>
            )}
          </div>
        )}
      </section>
    </div>,
    document.body,
  );
}
